import { useState } from 'react';

const services = [
  {
    icon: 'ri-phone-line',
    title: 'AI-Powered Reception & Response',
    description: 'Every inbound call, form, and message is captured and responded to instantly — 24/7, without additional staff.',
    bullets: ['Instant call handling', 'Multi-channel intake', 'Zero missed opportunities'],
    image: 'https://public.readdy.ai/ai/img_res/db4d96f99c7a054d3d2e149b5bd41b45.jpg',
  },
  {
    icon: 'ri-loop-right-line',
    title: 'Automated Follow-Up Sequences',
    description: 'Structured follow-up workflows that engage leads at the right moment, keeping your pipeline warm and conversion rates high.',
    bullets: ['Multi-touch sequences', 'Behavioral triggers', 'Conversion optimization'],
    image: 'https://readdy.ai/api/search-image?query=abstract%20workflow%20automation%20visualization%20with%20connected%20nodes%20and%20flowing%20data%20lines%20on%20dark%20background%2C%20dark%20charcoal%20tones%20with%20subtle%20lime%20green%20glow%2C%20minimal%20futuristic%20tech%20aesthetic%2C%20no%20people&width=600&height=520&seq=deliver002&orientation=portrait',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'CRM & Pipeline Management',
    description: 'Centralized visibility across every lead, deal, and interaction. Your entire pipeline in one intelligent system.',
    bullets: ['Unified dashboard', 'Lead scoring', 'Real-time tracking'],
    image: 'https://readdy.ai/api/search-image?query=dark%20CRM%20dashboard%20interface%20with%20charts%20graphs%20and%20pipeline%20visualization%20on%20very%20dark%20background%2C%20abstract%20data%20analytics%20aesthetic%2C%20dark%20charcoal%20with%20subtle%20accent%20colors%2C%20minimal%20futuristic%20design%2C%20no%20people&width=600&height=520&seq=deliver003&orientation=portrait',
  },
  {
    icon: 'ri-route-line',
    title: 'Lead Routing & Qualification',
    description: 'Intelligent workflows that score, qualify, and route every lead to the right team member at the right time.',
    bullets: ['Smart qualification', 'Auto-routing rules', 'Priority scoring'],
    image: 'https://readdy.ai/api/search-image?query=abstract%20lead%20routing%20network%20diagram%20with%20glowing%20connection%20lines%20and%20nodes%20on%20dark%20background%2C%20dark%20charcoal%20tones%20with%20subtle%20green%20accent%20glow%2C%20minimal%20futuristic%20tech%20visualization%2C%20no%20people&width=600&height=520&seq=deliver004&orientation=portrait',
  },
  {
    icon: 'ri-eye-line',
    title: 'Real-Time Performance Visibility',
    description: 'Track every metric that matters. Response times, conversion rates, pipeline velocity — all in real time.',
    bullets: ['Live dashboards', 'Performance alerts', 'Revenue attribution'],
    image: 'https://readdy.ai/api/search-image?query=real%20time%20analytics%20dashboard%20with%20glowing%20metrics%20and%20charts%20on%20very%20dark%20background%2C%20abstract%20data%20visualization%20with%20upward%20trending%20graphs%2C%20dark%20charcoal%20background%20with%20subtle%20lime%20green%20accent%20lights%2C%20no%20people&width=600&height=520&seq=deliver005&orientation=portrait',
  },
];

export default function DeliverSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section id="deliver" className="bg-[#0A0A0A] py-28 md:py-36">
      <div className="max-w-[1360px] mx-auto px-8 md:px-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-8 h-px bg-[#38C6F5]"></span>
            <span className="text-[#38C6F5] text-xs uppercase tracking-[3px]">What We Deliver</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight text-white leading-tight max-w-[640px]">
            Every system your business needs to{' '}
            <span className="font-semibold">never miss a lead</span>
          </h2>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Service List */}
          <div className="flex flex-col gap-2">
            {services.map((service, i) => (
              <button
                key={service.title}
                onClick={() => setActiveIndex(i)}
                className={`text-left p-6 rounded-xl transition-all duration-300 cursor-pointer group ${
                  activeIndex === i
                    ? 'bg-white/5 border border-white/10'
                    : 'border border-transparent hover:bg-white/3'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors duration-200 ${
                    activeIndex === i ? 'bg-[#38C6F5]/10 text-[#38C6F5]' : 'bg-white/5 text-[#707070] group-hover:text-white'
                  }`}>
                    <i className={`${service.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-base font-medium mb-2 transition-colors duration-200 ${
                      activeIndex === i ? 'text-white' : 'text-[#A0A0A0] group-hover:text-white'
                    }`}>
                      {service.title}
                    </h3>
                    {activeIndex === i && (
                      <div className="animate-[fadeIn_0.3s_ease]">
                        <p className="text-[#707070] text-sm leading-relaxed mb-4">{service.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {service.bullets.map((b) => (
                            <span key={b} className="text-xs text-[#38C6F5] bg-[#38C6F5]/10 px-3 py-1 rounded-full">
                              {b}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Right: Image */}
          <div className="hidden lg:block sticky top-28">
            <div className="relative w-full h-[520px] rounded-2xl overflow-hidden">
              <img
                src={services[activeIndex].image}
                alt={services[activeIndex].title}
                className="w-full h-full object-cover object-top transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <div className="w-8 h-8 flex items-center justify-center text-[#38C6F5] mb-3">
                  <i className={`${services[activeIndex].icon} text-2xl`}></i>
                </div>
                <p className="text-white text-lg font-light">{services[activeIndex].title}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
